import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowAvatar,
  AdminRowActionsButton,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminSupportingText,
  AdminRowMenuIcon,
  AdminLinkAction,
  AdminStatusBadge,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import defaultUserAvatar from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  ARCHIVE_DECISION_OUTCOME_OPTIONS,
  ARCHIVE_FINALISED_DATE_OPTIONS,
  collectArchiveCases,
  formatQueueDateTime,
  matchesArchiveFilters,
  matchesArchiveSearch,
} from '../../../data/moderationArchiveSpec';
import { getTypeFilterOptions } from '../../../data/moderationQueueSpec';

const DEFAULT_FILTERS = {
  type: 'All',
  decisionOutcome: 'All',
  dateRange: 'All',
};

/** Case ID · Account · Source · Type · Decision Outcome · Decision ID · Finalised On · Actions (old order + Decision ID) */
const TABLE_GRID =
  'minmax(0, 1.15fr) minmax(0, 1.2fr) minmax(0, 0.7fr) minmax(0, 0.7fr) minmax(0, 1.05fr) minmax(0, 1.1fr) minmax(0, 1.05fr) minmax(62px, auto)';

const Violations = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [typeOpen, setTypeOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [items] = useState(() => collectArchiveCases());
  const [currentPage, setCurrentPage] = useState(1);

  const typeRef = useRef(null);
  const outcomeRef = useRef(null);
  const dateRef = useRef(null);
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: rowActionsMenuLayout,
    menuRef: rowActionsMenuRef,
    placementReady: rowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.SINGLE
  );

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));
  useClickOutside(dateRef, () => setDateOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const filteredCases = useMemo(
    () =>
      items.filter(
        (row) =>
          matchesArchiveSearch(row, debouncedSearch) && matchesArchiveFilters(row, filters)
      ),
    [items, debouncedSearch, filters]
  );

  const totalCount = filteredCases.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(filteredCases.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [filteredCases.length]);

  const paginatedCases = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return filteredCases.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [filteredCases, effectivePage]);

  const handleViewCase = (item, e) => {
    if (e) e.stopPropagation();
    navigate(`/admin/moderation/queue/${item.caseId}`, {
      state: { readOnly: true, item, from: location.pathname },
    });
  };

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Moderation Archive</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Case ID, Decision ID, Flag ID, Appeal…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search archived Moderation cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Finalised Date"
                value={filters.dateRange}
                options={ARCHIVE_FINALISED_DATE_OPTIONS}
                onChange={(value) => handleFilterChange('dateRange', value)}
                menuRef={dateRef}
                open={dateOpen}
                setOpen={setDateOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Decision Outcome"
                value={filters.decisionOutcome}
                options={ARCHIVE_DECISION_OUTCOME_OPTIONS}
                onChange={(value) => handleFilterChange('decisionOutcome', value)}
                menuRef={outcomeRef}
                open={outcomeOpen}
                setOpen={setOutcomeOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={getTypeFilterOptions()}
                onChange={(value) => handleFilterChange('type', value)}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: TABLE_GRID }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Account</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Source</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Decision Outcome</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Decision ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Finalised On</AdminTableHeader>
                </div>
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedCases.length === 0 ? (
                <div className="text-center py-10">
                  <AdminSupportingText className="text-mediumgray">
                    No archived cases match your filters.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedCases.map((item) => (
                  <div
                    key={item.caseId}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleViewCase(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewCase(item);
                      }
                    }}
                    className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                    style={{ gridTemplateColumns: TABLE_GRID }}
                  >
                    <div className="flex items-center min-w-0">
                      <AdminLinkAction type="button" onClick={(e) => handleViewCase(item, e)}>
                        {item.caseId}
                      </AdminLinkAction>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <AdminRowAvatar
                        src={item.accountAvatar || defaultUserAvatar}
                        alt={item.accountName}
                        onError={(e) => {
                          e.target.src = defaultUserAvatar;
                        }}
                      />
                      <AdminTableBody emphasis className="truncate" title={item.accountName}>
                        {item.accountName}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate">{item.source}</AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={item.type}>
                        {item.type}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminStatusBadge
                        status={item.decisionOutcome}
                        label={item.decisionOutcome}
                      />
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableBody className="truncate" title={item.moderationDecisionId}>
                        {item.moderationDecisionId || '—'}
                      </AdminTableBody>
                    </div>
                    <div className="flex items-center min-w-0">
                      <AdminTableMeta className="whitespace-nowrap">
                        {formatQueueDateTime(item.finalisedOn)}
                      </AdminTableMeta>
                    </div>
                    <div
                      className="flex items-center justify-center shrink-0 overflow-visible"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="relative inline-flex"
                        ref={(el) => {
                          rowActionsRefs.current[item.caseId] = el;
                        }}
                      >
                        <AdminRowActionsButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowActions(item.caseId);
                          }}
                          aria-expanded={Boolean(rowActionsOpen[item.caseId])}
                          title="Actions"
                          aria-label="Row actions"
                        >
                          <AdminRowMenuIcon />
                        </AdminRowActionsButton>
                        {rowActionsOpen[item.caseId] && rowActionsMenuLayout ? (
                          <AdminFixedRowActionsMenu
                            layout={rowActionsMenuLayout}
                            menuRef={rowActionsMenuRef}
                            placementReady={rowActionsMenuPlacementReady}
                          >
                            <AdminRowMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setRowActionsOpen({});
                                handleViewCase(item, e);
                              }}
                            >
                              View
                            </AdminRowMenuItem>
                          </AdminFixedRowActionsMenu>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {showPagination ? (
            <ConfigurationListPaginationFooter
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalCount={totalCount}
              page={effectivePage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : null}
        </DashboardMainContainer>
      </div>
    </AdminLayout>
  );
};

export default Violations;
